import React from "react";
import { Link, useLocation } from "react-router";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import ChargeCalculationRow from "./ChargeCalculationRow";
import getBillingLabel from "../../utils/billing/getBillingLabel";
import useAuth from "../../hooks/useAuth";

const sectionToRateKey = {
  laborCharges: "laborRates",
  materialCharges: "materialRates",
  equipmentCharges: "equipmentRates",
  openInspectCharges: "openInspectRates",
};

export default function CategoryChargesInfoModal({
  open = true,
  onClose,
  title,
  charges = {},
  onAddCharges,
  onEdit,
  project,
  entity,
  categoryKey,
}) {
  const location = useLocation();
  const { user } = useAuth();
  const isInternalStaff = ["admin", "manager", "inventory"].includes(user?.role);
  const rateKey = sectionToRateKey[categoryKey];
  const canAddCharges = !!rateKey && !!project && !!entity;
  const chargeKeys = Object.keys(charges).filter(key => !["calculated_at", "total"].includes(key));
  const hasCharges = chargeKeys.length > 0;
  const canEditBillableQuantities =
    (categoryKey === "inboundCharges" || categoryKey === "outboundCharges") &&
    typeof onEdit === "function";

  const handleAddChargesClick = () => {
    if (!canAddCharges) {
      if (onAddCharges) onAddCharges();
      return;
    }
    if (onEdit) onEdit();
  };

  const getEditRatesTo = () => {
    const returnParams = new URLSearchParams(location.search);
    returnParams.set("returnedFromEditRates", "true");
    const returnTo = `${location.pathname}?${returnParams.toString()}${location.hash || ""}`;
    const searchParams = new URLSearchParams();
    searchParams.set("info", "true");
    searchParams.set("tab", "settings");

    const isBillingType = ["inboundCharges", "outboundCharges"].includes(categoryKey);
    const billingConfig = isBillingType ? "billing-types" : "ancillary-rates";

    searchParams.set("billingConfig", billingConfig);
    searchParams.set("billingSection", categoryKey.replace("Charges", ""));
    searchParams.set("search", chargeKeys.map(key => getBillingLabel(key)).join(","));
    searchParams.set("returnTo", returnTo);

    return {
      search: searchParams.toString(),
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography fontSize={20} fontWeight={500} color="text.primary" align="center">
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          color="text.secondary"
          mt={hasCharges ? 0 : 3}
          mb={hasCharges ? 0 : 3}
          px={3}
          textAlign="center"
          fontSize={15}
        >
          {hasCharges
            ? `This screen shows you how the ${(title || "").toLowerCase()} for this transaction were calculated`
            : `This transaction doesn't have any ${(title || "").toLowerCase()}. Select to the button to add some if you'd like`}
        </Box>
        <Box display="flex" flexDirection="column" gap={2}>
          {hasCharges &&
            chargeKeys.map(key => {
              const row = charges[key];
              const label = getBillingLabel(key);
              const isHundredweight = key === "hundredweight";
              const hasMinimumCharge = row?.notes?.includes("Minimum charge applied");
              return (
                <ChargeCalculationRow
                  key={key}
                  label={label}
                  rateUsed={row?.rate_used}
                  quantity={row?.quantity}
                  total={row?.total}
                  notes={row?.notes}
                  isHundredweight={isHundredweight && !hasMinimumCharge}
                />
              );
            })}
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} gap={1}>
          {isInternalStaff && hasCharges && (
            <Button variant="text" component={Link} to={getEditRatesTo()}>
              Edit Rates
            </Button>
          )}
          <Box display="flex" gap={1} ml="auto">
            {isInternalStaff && canEditBillableQuantities && (
              <Button variant="outlined" color="primary" onClick={onEdit}>
                {hasCharges ? "Edit Billable Quantities" : "Add Billable Quantities"}
              </Button>
            )}
            {isInternalStaff && canAddCharges && (
              <Button variant="outlined" color="primary" onClick={handleAddChargesClick}>
                {hasCharges ? "Edit / Add Charges" : "Add Charges"}
              </Button>
            )}
            {isInternalStaff && !canAddCharges && !hasCharges && onAddCharges && (
              <Button variant="outlined" color="primary" onClick={onAddCharges}>
                Add Charges
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
